/** Fleet Vehicles — cross-tenant roster grid (List tab).
 *
 * Standard console ResourceDataGrid: presets, search, manageable columns,
 * tenant/status/assigned/enabled filters, and batch delete + unassign. Rows open
 * the edit drawer; a per-row menu covers assign/unassign/delete. Mirrors the
 * dashboard `VehicleList` columns plus a Tenant column (this surface is
 * cross-tenant, unlike the dashboard's single-operator view).
 */
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Box, Button, Chip, IconButton, Link, Menu, MenuItem, Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import * as fleetVehiclesApi from '@/api/fleetVehicles';
import type { Vehicle } from '@/api/fleetVehicles';
import { deleteBatchAction, type BatchActionDef } from '@/components/DataGrid/batchTypes';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import { copyableColumn } from '@/components/DataGrid/copyableColumn';
import { ResourceDataGrid, type ResourceDataGridParams } from '@/components/DataGrid/ResourceDataGrid';
import { ResponsiveFilterBar, type FilterBarItem } from '@/components/DataGrid/ResponsiveFilterBar';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useSnackbar } from '@/components/Snackbar';
import { TenantLinkCell } from '@/components/TenantMark/TenantMark';
import { TenantPicker } from '@/components/TenantPicker/TenantPicker';
import { useGridFilters, type FilterDef } from '@/hooks/useGridFilters';
import { useCreateIntent } from '@/hooks/useCreateIntent';
import { usePermissions } from '@/hooks/usePermissions';
import { makeResourceFiltersAdapter, useGridPresets } from '@/hooks/useGridPresets';
import { pageTenantsFrom } from '@/scope/pageTenants';
import { formatTenantIds, tenantIdsToApi } from '@/scope/tenantIds';
import { useTenantScopeList } from '@/scope/tenantScope';

import { AssignRouteDialog } from './AssignRouteDialog';
import { DEFAULT_MARKER_COLOR, fmtTs, formatAge, googleMapsLink, statusMeta, VEHICLE_STATUSES } from './shared';
import { VehicleEditDialog } from './VehicleEditDialog';

const QUERY_KEY = ['fleet-vehicles'] as const;

const passthrough = (v: string) => v || undefined;

const FILTERS: FilterDef[] = [
  { param: 'tenant', apiParam: 'tour_operator_id', toApi: tenantIdsToApi, tenantScope: 'multi' },
  { param: 'status', toApi: passthrough },
  { param: 'assigned', toApi: passthrough },
  { param: 'enabled', toApi: passthrough },
];

const PRESET_FILTERS = makeResourceFiltersAdapter(FILTERS);

const DEFAULT_ORDER = [
  'id', 'name', 'license_plate', 'tenant', 'enabled', 'status', 'location', 'route',
  'status_ts', 'icon', 'type_name', 'tags', 'route_hints', 'capacity', 'ext_id',
  'route_assignment_ts', 'last_sync_ts', 'created_ts', 'updated_ts', 'actions',
];
const DEFAULT_HIDDEN = [
  'type_name', 'tags', 'route_hints', 'capacity', 'ext_id', 'route_assignment_ts',
  'last_sync_ts', 'created_ts', 'updated_ts',
];

const DASH = <Typography variant="caption" color="text.disabled">—</Typography>;

export function VehiclesListTab() {
  const { hasPerm } = usePermissions();
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const canManage = hasPerm('console_fleet_manage');

  const [editOpen, setEditOpen] = useState(false);
  const [editVehicle, setEditVehicle] = useState<Vehicle | null>(null);
  useCreateIntent(() => {
    setEditVehicle(null);
    setEditOpen(true);
  });
  const [assignVehicle, setAssignVehicle] = useState<Vehicle | null>(null);
  const [menu, setMenu] = useState<{ el: HTMLElement; vehicle: Vehicle } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Vehicle | null>(null);

  const presets = useGridPresets({
    grid: 'vehicles',
    defaultOrder: DEFAULT_ORDER,
    defaultHidden: DEFAULT_HIDDEN,
    defaultOrdering: 'name',
    filters: PRESET_FILTERS,
  });

  const {
    extra, getParam, setParam, resetAll: resetAllFilters, anyActive: anyFilterActive,
  } = useGridFilters(FILTERS, presets.filtersLayer);

  // The operators the effective tenant filter names (URL > preset > scope): the
  // picker's value, and what a create dialog pre-selects / features (D9).
  const pageTenants = pageTenantsFrom(getParam('tenant'), useTenantScopeList());
  const statusFilter = getParam('status') ?? 'all';
  const assignedFilter = getParam('assigned') ?? 'all';
  const enabledFilter = getParam('enabled') ?? 'all';

  const invalidate = () => void qc.invalidateQueries({ queryKey: QUERY_KEY });
  const onError = (e: unknown, fallback: string) =>
    snackbar.show(e instanceof ConsoleApiError ? e.message : fallback, 'error');

  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: number; enabled: boolean }) =>
      fleetVehiclesApi.update(id, { enabled }),
    onSuccess: invalidate,
    onError: (e) => onError(e, 'Could not update the vehicle.'),
  });
  const unassignMutation = useMutation({
    mutationFn: (id: number) => fleetVehiclesApi.unassignRoute(id),
    onSuccess: () => { snackbar.show('Route unassigned.', 'success'); invalidate(); },
    onError: (e) => onError(e, 'Could not unassign the route.'),
  });
  const deleteMutation = useMutation({
    mutationFn: (id: number) => fleetVehiclesApi.remove(id),
    onSuccess: () => { snackbar.show('Vehicle deleted.', 'success'); invalidate(); },
    onError: (e) => onError(e, 'Could not delete the vehicle.'),
  });

  const columns = useMemo<GridColDef<Vehicle>[]>(() => [
    copyableColumn({ field: 'id', headerName: 'ID', width: 80 }),
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 160 },
    { field: 'license_plate', headerName: 'License plate #', width: 140 },
    {
      field: 'tenant', headerName: 'Operator', width: 170, sortable: false, filterable: false,
      valueGetter: (_, row) => row.tenant?.name ?? '',
      renderCell: (p) => <TenantLinkCell tenant={p.row.tenant} empty={DASH} />,
    },
    {
      field: 'enabled', headerName: 'Enabled', width: 90, type: 'boolean',
      renderCell: (p) => (
        <Switch
          size="small" checked={p.row.enabled} disabled={!canManage || toggleMutation.isPending}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => toggleMutation.mutate({ id: p.row.id, enabled: e.target.checked })}
        />
      ),
    },
    {
      field: 'status', headerName: 'Status', width: 130, sortable: true,
      renderCell: (p) => {
        const m = statusMeta(p.row.status);
        return <Chip size="small" label={m.label} color={m.color} variant={m.color === 'default' ? 'outlined' : 'filled'} />;
      },
    },
    {
      field: 'location', headerName: 'Location', width: 160, sortable: false, filterable: false,
      valueGetter: (_, r) => (r.latitude != null && r.longitude != null ? `${r.latitude},${r.longitude}` : ''),
      renderCell: (p) => p.row.latitude != null && p.row.longitude != null ? (
        <Link
          href={googleMapsLink(p.row.latitude, p.row.longitude)} target="_blank" rel="noopener"
          onClick={(e) => e.stopPropagation()}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontFamily: 'monospace', fontSize: 12 }}
        >
          {p.row.latitude.toFixed(4)}, {p.row.longitude.toFixed(4)}
          <OpenInNewIcon sx={{ fontSize: 12 }} />
        </Link>
      ) : DASH,
    },
    {
      field: 'route', headerName: 'Assigned route', width: 160, sortable: false, filterable: false,
      valueGetter: (_, r) => r.route_name ?? '',
      renderCell: (p) => p.row.route ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: p.row.route.color || DEFAULT_MARKER_COLOR, flexShrink: 0 }} />
          <span>{p.row.route.name}</span>
        </Box>
      ) : DASH,
    },
    {
      field: 'status_ts', headerName: 'Status update', width: 150,
      renderCell: (p) => p.row.status_ts ? (
        <Tooltip title={fmtTs(p.row.status_ts)}><span>{formatAge(p.row.status_ts)}</span></Tooltip>
      ) : DASH,
    },
    {
      field: 'icon', headerName: 'Icon', width: 70, sortable: false, filterable: false,
      renderCell: (p) => p.row.icon?.url ? (
        <Box component="img" src={p.row.icon.url} alt="" sx={{ width: 28, height: 28, objectFit: 'contain' }} />
      ) : DASH,
    },
    {
      field: 'type_name', headerName: 'Type', width: 130, sortable: false, filterable: false,
      valueGetter: (_, r) => r.type_name ?? '',
      renderCell: (p) => p.row.type_name ? <span>{p.row.type_name}</span> : DASH,
    },
    {
      field: 'tags', headerName: 'Tags', flex: 1, minWidth: 160, sortable: false, filterable: false,
      valueGetter: (_, r) => (r.tags ?? []).map((t) => t.name).join(', '),
      renderCell: (p) => (p.row.tags && p.row.tags.length > 0)
        ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 0.5 }}>
            {p.row.tags.map((t) => <Chip key={t.id} size="small" variant="outlined" label={t.name} />)}
          </Box>
        )
        : DASH,
    },
    {
      field: 'route_hints', headerName: 'Route hints', flex: 1, minWidth: 160, sortable: false, filterable: false,
      valueGetter: (_, r) => (r.route_hints ?? []).join(', '),
      renderCell: (p) => (p.row.route_hints && p.row.route_hints.length > 0)
        ? <Tooltip title={p.row.route_hints.join(', ')}><span>{p.row.route_hints.join(', ')}</span></Tooltip>
        : DASH,
    },
    // Not in the backend _ORDERABLE whitelist — a header sort would silently fall
    // back to name, so keep these display-only.
    { field: 'capacity', headerName: 'Capacity', width: 100, type: 'number', sortable: false },
    { field: 'ext_id', headerName: 'External ID', width: 150, sortable: false },
    {
      field: 'route_assignment_ts', headerName: 'Route assigned', width: 160, sortable: false,
      renderCell: (p) => <span>{fmtTs(p.row.route_assignment_ts)}</span>,
    },
    {
      field: 'created_ts', headerName: 'Created', width: 160,
      renderCell: (p) => <span>{fmtTs(p.row.created_ts)}</span>,
    },
    {
      field: 'updated_ts', headerName: 'Updated', width: 160,
      renderCell: (p) => <span>{fmtTs(p.row.updated_ts)}</span>,
    },
    {
      // last_sync_ts is NOT in the backend _ORDERABLE whitelist — a header sort
      // would silently fall back to name, so keep it display-only.
      field: 'last_sync_ts', headerName: 'Last sync', width: 160, sortable: false,
      renderCell: (p) => <span>{fmtTs(p.row.last_sync_ts)}</span>,
    },
    {
      field: 'actions', headerName: '', width: 56, sortable: false, filterable: false, align: 'right',
      renderCell: (p) => (
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); setMenu({ el: e.currentTarget, vehicle: p.row }); }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
  ], [canManage, toggleMutation]);

  const orderingFallback = presets.ordering;
  const queryFn = useCallback(
    (params: ResourceDataGridParams) =>
      fleetVehiclesApi.list({
        page: params.page,
        pageSize: params.pageSize,
        ordering: params.ordering ?? orderingFallback ?? 'name',
        search: params.search,
        ...(params.extra as Record<string, never>),
      }),
    [orderingFallback],
  );

  const toolbarItems: FilterBarItem[] = [
    {
      key: 'tenant', priority: 5, active: pageTenants.length > 0,
      node: (
        <Box sx={{ minWidth: 280 }}>
          <TenantPicker
            multiple
            value={pageTenants}
            onChange={(next) => setParam('tenant', formatTenantIds(next.map((t) => t.id)))}
          />
        </Box>
      ),
    },
    {
      key: 'status', priority: 4, active: statusFilter !== 'all',
      node: (
        <TextField
          select size="small" label="Status" value={statusFilter} sx={{ width: 150 }}
          onChange={(e) => setParam('status', e.target.value === 'all' ? null : e.target.value)}
        >
          <MenuItem value="all">Any status</MenuItem>
          {VEHICLE_STATUSES.map((s) => (
            <MenuItem key={s} value={s}>{statusMeta(s).label}</MenuItem>
          ))}
        </TextField>
      ),
    },
    {
      key: 'assigned', priority: 3, active: assignedFilter !== 'all',
      node: (
        <TextField
          select size="small" label="Route" value={assignedFilter} sx={{ width: 150 }}
          onChange={(e) => setParam('assigned', e.target.value === 'all' ? null : e.target.value)}
        >
          <MenuItem value="all">Any</MenuItem>
          <MenuItem value="true">Assigned</MenuItem>
          <MenuItem value="false">Unassigned</MenuItem>
        </TextField>
      ),
    },
    {
      key: 'enabled', priority: 2, active: enabledFilter !== 'all',
      node: (
        <TextField
          select size="small" label="Enabled" value={enabledFilter} sx={{ width: 130 }}
          onChange={(e) => setParam('enabled', e.target.value === 'all' ? null : e.target.value)}
        >
          <MenuItem value="all">Any</MenuItem>
          <MenuItem value="true">Enabled</MenuItem>
          <MenuItem value="false">Disabled</MenuItem>
        </TextField>
      ),
    },
  ];

  const TENANT = 'console_fleet_manage';
  const batchActions: BatchActionDef[] = [
    {
      key: 'unassign', label: 'Unassign from route', action: 'unassign', permission: TENANT,
      confirmText: (n) => `Clear the route assignment on ${n.toLocaleString()} vehicle(s)?`,
    },
    deleteBatchAction(TENANT),
  ];

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 1, flexWrap: 'wrap' }}>
        <ResponsiveFilterBar items={toolbarItems} />
        {canManage && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => { setEditVehicle(null); setEditOpen(true); }}
          >
            Add vehicle
          </Button>
        )}
      </Box>

      {presets.blocking ? null : (
        <ResourceDataGrid<Vehicle>
          queryKey={QUERY_KEY}
          queryFn={queryFn}
          columns={orderColumns(columns, presets.orderedFields)}
          extra={extra}
          batchResource="vehicles"
          batchActions={canManage ? batchActions : []}
          defaultSort={{ field: 'name', sort: 'asc' }}
          sortModel={presets.sortModel}
          onSortModelChange={presets.onSortModelChange}
          columnVisibilityModel={presets.columnVisibilityModel}
          onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
          onColumnOrderChange={presets.onColumnOrderChange}
          onRowClick={(_, row) => { setEditVehicle(row); setEditOpen(true); }}
          emptyMessage="No vehicles."
          headerToolbar={{
            onResetAllFilters: resetAllFilters,
            resetDisabled: !anyFilterActive,
            search: true,
            searchPlaceholder: 'Name / plate / external ID',
            exportFileName: 'vehicles',
            presets: presets.toolbar,
          }}
        />
      )}

      <Menu anchorEl={menu?.el ?? null} open={menu !== null} onClose={() => setMenu(null)}>
        <MenuItem
          disabled={!canManage}
          onClick={() => { if (menu) setAssignVehicle(menu.vehicle); setMenu(null); }}
        >
          Assign to route…
        </MenuItem>
        <MenuItem
          disabled={!canManage || !menu?.vehicle.route}
          onClick={() => { if (menu) unassignMutation.mutate(menu.vehicle.id); setMenu(null); }}
        >
          Unassign route
        </MenuItem>
        <MenuItem
          disabled={!canManage}
          onClick={() => { const v = menu?.vehicle; setMenu(null); if (v) setConfirmDelete(v); }}
        >
          Delete
        </MenuItem>
      </Menu>

      <VehicleEditDialog
        open={editOpen}
        vehicle={editVehicle}
        onClose={() => setEditOpen(false)}
        onSaved={invalidate}
        pageTenants={pageTenants}
      />
      <AssignRouteDialog
        open={assignVehicle !== null}
        vehicle={assignVehicle}
        onClose={() => setAssignVehicle(null)}
        onAssigned={invalidate}
      />
      <ConfirmDialog
        open={confirmDelete !== null}
        title="Delete vehicle?"
        message={`This permanently removes "${confirmDelete?.name ?? ''}" from the fleet.`}
        confirmLabel="Delete"
        destructive
        onConfirm={() => { if (confirmDelete) deleteMutation.mutate(confirmDelete.id); setConfirmDelete(null); }}
        onCancel={() => setConfirmDelete(null)}
      />
    </>
  );
}
